import { Activity, type ReactNode, useCallback } from "react";
import { Columns2Icon, Rows2Icon } from "lucide-react";

import { detailTypenameForView } from "@/app/app/_/layouts/nodePanelConfig";
import { Icon } from "@/components/atoms/Icon";
import { useAppRouteNavigation, useAppRouteState } from "@/app/app/_/routing";
import type { IObjectWithTypename } from "@/components/@types";
import { NodeLayout } from "@/components/organisms/Node/NodeLayout";
import { NodeTitleLayout } from "@/components/organisms/Node/NodeTitleLayout";
import { useNode, useNodeSetup } from "@/components/organisms/Node/Node.hooks";
import { usePageThreadReference } from "@/components/organisms/Thread/Thread.hooks";
import { HotkeyScope, useContextHotkeys } from "@/hooks/useContextTree";
import { useDevice } from "@/hooks/useDevice";
import { StickyLayout } from "@/components/organisms/StickyLayout";
import { Queries } from "@/constants";
import { useBackendQuery as useQuery } from "@/hooks/useBackendQuery";
import { identifierAndTypenameToId } from "@/objects/@utils";
import { State } from "@/state";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

/**
 * Full-width detail page for a routed record (``/app/documents/all/<id>``).
 *
 * It renders the same node HOC as the panel and the fullscreen dialog, and owns
 * its store the way the dialog does — the panel store cannot be shared, because
 * a reference drill-down from this page repoints that store at the *related*
 * record.
 *
 * The page defaults to the "cards" arrangement — groups become side-by-side
 * columns instead of tabs, which is what makes this read as a page rather than a
 * wide panel — and the header toggle stores a per-typename preference in
 * ``nodeDetailLayoutModes``. Either way ``NodeLayout``'s width probe has the
 * final say: too narrow for the columns and it falls back to tabs. The
 * route also writes the record into ``activeTabNodeIds[view][tab]`` (see
 * ``routing.tsx``) — the slot rules and reports already use for their detail
 * records — so the breadcrumb detail crumb and its "back to list" work here
 * without any page-specific chrome.
 */
function NodeDetailLayoutContent({
  nodeId,
  typename,
}: {
  nodeId: string;
  typename: string;
}) {
  const { data, loading } = useQuery(Queries.NODE_QUERY_WITH_PENDING_CHANGE, {
    variables: {
      id: nodeId,
      startDate: State.getState().timeSeriesFilter.startDate?.toISOString(),
      endDate: State.getState().timeSeriesFilter.endDate?.toISOString(),
    },
  });
  const node = data?.node as IObjectWithTypename | undefined;
  const isLoading = loading && node == null;
  const { store } = useNodeSetup({
    mode: "update",
    // Seed the store with the id from the route while the node is in flight.
    // Group data (document line items) is fetched off the store's id, so
    // waiting for the node first serialises two slow round trips; with the id
    // known up front they run side by side. ``useNodeSetup`` already expects a
    // stub like this — it replaces near-empty field values once the node lands.
    node: (node ?? {
      id: nodeId,
      __typename: typename,
    }) as IObjectWithTypename,
    typename,
  });
  const { actions, metadata } = useNode(store);
  const { isNarrow } = useDevice();
  // The record open here goes to the chat as the page's, for as long as it
  // is open.
  usePageThreadReference(
    node
      ? {
          id: node.id,
          identifier: node.identifier,
          name: metadata.title || "Untitled",
          typename,
        }
      : null,
  );
  const layoutMode = State(
    (state) => state.nodeDetailLayoutModes[typename] ?? "split",
  );

  return (
    // The one place in the content area that is not ``content``: a detail page
    // is a record rather than a list of them, so it sits on ``main`` like the
    // surfaces around it instead of the recessed list background. It fills
    // the content area's column as a share of it rather than ``h-full``:
    // Safari lays a percentage height out again through every column under
    // it, at every key typed into the page.
    <StickyLayout
      className="flex-1 h-auto bg-background"
      stickyTopChildren={
        <NodeTitleLayout
          store={store}
          title={metadata.title}
          isLoading={isLoading}
          actions={actions}
          trailingDecorator={
            // Preference, not an override: ``preferredLayoutMode`` still
            // defers to the width probe, so a window too narrow for the
            // columns falls back to tabs regardless of what is stored - and
            // a phone's screen, or a window as narrow, always too narrow,
            // has no toggle to press.
            isNarrow ? undefined : (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    onClick={() =>
                      State.getState().setNodeDetailLayoutMode(
                        typename,
                        layoutMode === "split" ? "tabs" : "split",
                      )
                    }
                  >
                    <Icon
                      ReactIcon={
                        layoutMode === "split" ? Rows2Icon : Columns2Icon
                      }
                    />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="bottom">
                  {layoutMode === "split" ? "Tab view" : "Split view"}
                </TooltipContent>
              </Tooltip>
            )
          }
        />
      }
      stickyTopClassName="[border-bottom-width:1px] [border-bottom-style:solid] border-border"
    >
      <div className="flex flex-col flex-1 min-h-0 overflow-hidden">
        <NodeLayout
          store={store}
          isLoading={isLoading}
          isSingleClick={true}
          isMultiline={true}
          preferredLayoutMode={layoutMode === "split" ? "cards" : "tabs"}
        />
      </div>
    </StickyLayout>
  );
}

/**
 * A list view and, over it, the routed page of one of its records.
 *
 * The list stays mounted while a record is open, hidden in an ``Activity``:
 * leaving the record shows the list exactly as it was left — rows, loaded
 * pages, scroll — rather than rebuilding it from the cache behind its loading
 * skeleton. Hidden, its effects (queries, hotkeys) are torn down; shown again,
 * they re-run, which is when the table refocuses the cell the record was
 * opened from.
 */
export function NodeDetailLayout({ children }: { children: ReactNode }) {
  const routeState = useAppRouteState();
  const appRouteNavigation = useAppRouteNavigation();
  const typename = detailTypenameForView(routeState.view);
  const backToList = useCallback(
    () => appRouteNavigation.navigateTo({ detailIdentifier: null }),
    [appRouteNavigation],
  );

  // Escape leaves the record for the list it was opened from — the keyboard
  // twin of the breadcrumb's "back to list", and what the panel's Escape does
  // one level in. Global scope because focus is usually nowhere in particular
  // by the time the page renders: drilling in from a reference chip unmounts
  // the element that was focused. Anything nearer — a popper, a focused cell
  // returning to its nav — is a ``within`` handler and wins, so this only
  // fires once the page itself is what Escape is aimed at. Back on the list,
  // the table refocuses the cell the page was opened from
  // (``detailOriginCell``).
  useContextHotkeys({
    id: "node-detail-back-to-list",
    combos: "Escape",
    callback: backToList,
    scopeCheck: HotkeyScope.global,
    isEnabled: Boolean(typename && routeState.detailIdentifier),
  });

  const { detailIdentifier } = routeState;

  return (
    <>
      <Activity mode={typename && detailIdentifier ? "hidden" : "visible"}>
        {children}
      </Activity>
      {typename && detailIdentifier && (
        <NodeDetailLayoutContent
          // A different record gets a fresh store rather than reconciling the
          // previous record's pending state onto it.
          key={detailIdentifier}
          nodeId={identifierAndTypenameToId(detailIdentifier, typename)}
          typename={typename}
        />
      )}
    </>
  );
}
